import { normalizeDate } from "../normalize/index.js";
import type { ExcelImportRow } from "../schema/import.js";

/**
 * 기존 `방화벽.xlsx` 한 행 → 이관 요청 행.
 * 진실의 원천: harness/API-SPEC.md §11-1, docs/01_아키텍처와_데이터모델.md §9-2.
 *
 * 이 매핑이 **왜 공용 패키지에 있는가**: 열 이름은 화면 사정이 아니라 계약이다.
 * 화면 컴포넌트 안에 두면 실제 파일과 어긋나도 아무도 실행해 보지 않는다 —
 * 실제로 `비고2` 라는 이름으로 J 열을 읽고 있었고, 그 이름은 원본에 없다.
 */

/** 원본 머리글 (2026-09 실측 — 두 번째 `비고` 는 머리글이 첫 번째와 **같다**) */
export const LEGACY_EXCEL_HEADERS = [
  "일자",
  "업체이름",
  "시리얼",
  "담당자",
  "연락처",
  "비고",
  "출고일자",
  "행선지",
  "송장번호",
  "비고", // J 열 — 기타 메모. 머리글이 F 열과 중복된다
] as const;

/**
 * J 열(두 번째 `비고`)을 찾는 열쇠.
 *
 * 중복 머리글을 파서가 어떻게 구분해 주는지는 파서 사정이다 — SheetJS 는 `비고_1`,
 * 다른 도구는 `비고2`·`비고 2`·`__EMPTY` 를 쓴다. **이름 하나만 보고 있으면 J 열이
 * 조용히 사라진다**(원본 91행 중 2행이 그렇게 사라지고 있었다). 그래서 이름을
 * 고르지 않고, "첫 번째가 아닌 비고" 를 전부 후보로 본다.
 */
const MEMO2_KEY = /^비고[\s_]*\d+$/;

function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}

/**
 * `*_raw` 열에 들어갈 값은 **앞뒤 공백까지 그대로** 둔다 (SOUL S3, DATA-DICTIONARY §1).
 *
 * 시리얼과 송장은 정규화한 값과 원문을 따로 저장하는 유일한 두 항목이다. 여기서
 * trim 을 해 버리면 `devices.serial_raw` 가 "원문" 이 아니라 "조금 다듬은 원문" 이
 * 되고, 나중에 원본과 대조할 근거가 사라진다. 실물 파일 20행이 앞에 공백 하나를
 * 달고 있었고, 그 공백이 여기서 사라지고 있었다.
 *
 * 공백뿐인 칸은 "없음" 으로 본다 — 그건 원문이 아니라 빈 칸이다.
 */
function rawText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value);
  return s.trim() === "" ? null : s;
}

/**
 * 날짜 칸은 셀 서식에 따라 Date · 엑셀 직렬값 · 문자열이 섞여 들어온다.
 *
 * 엑셀의 날짜 셀은 **"그 날"이지 순간이 아니다.** 파서(SheetJS)는 그 날의
 * *현지* 자정으로 Date 를 만든다 — `toISOString()` 을 쓰면 KST 자정이 UTC 로
 * 9시간 밀려 **모든 날짜가 하루 앞당겨진다**(실측: 2026-08-04 → 2026-08-03).
 * 그래서 UTC 로 옮기지 않고 현지 연·월·일을 그대로 읽는다.
 */
function dateCell(value: unknown): string | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = String(value.getFullYear()).padStart(4, "0");
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return normalizeDate(`${y}-${m}-${d}`);
  }
  if (typeof value === "number" || typeof value === "string") return normalizeDate(value);
  return null;
}

/** 두 번째 `비고` 를 이름을 몰라도 찾는다 (첫 번째 `비고` 는 F 열 = 증상) */
export function pickSecondMemo(raw: Record<string, unknown>): string | null {
  for (const key of Object.keys(raw)) {
    if (key === "비고") continue;
    if (MEMO2_KEY.test(key)) {
      const value = text(raw[key]);
      if (value !== null) return value;
    }
  }
  return null;
}

/**
 * @param raw    파서가 준 한 행 (머리글 → 값)
 * @param index  0부터 세는 데이터 행 번호. 원본 엑셀 행 번호는 +2 (1행이 머리글)
 */
export function toLegacyExcelRow(raw: Record<string, unknown>, index: number): ExcelImportRow {
  const shippedAtRaw = text(raw["출고일자"]);
  return {
    // 날짜를 추정해 채우지 않는다 (SOUL S2) — 못 읽으면 null 로 두고 리포트에 남는다
    received_at: dateCell(raw["일자"]),
    company_raw: text(raw["업체이름"]) ?? "",
    serial_raw: rawText(raw["시리얼"]) ?? "",
    sender_name: text(raw["담당자"]),
    sender_contact: text(raw["연락처"]),
    memo: text(raw["비고"]),
    shipped_at: dateCell(raw["출고일자"]),
    // 날짜로 못 읽은 출고일자 원문을 함께 보낸다. 이게 없으면 "출고안함"·"-" 같은
    // 값이 아무 말 없이 사라진다 — 조용한 실패는 실패가 아니라고 말하는 것과 같다(S1)
    shipped_at_raw: shippedAtRaw,
    destination_raw: text(raw["행선지"]),
    tracking_raw: rawText(raw["송장번호"]),
    memo2: pickSecondMemo(raw),
    source_row_no: index + 2,
  };
}
